import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  getBackupOnboardingStep,
  setBackupOnboardingStep,
  type IOnboardingStep,
} from 'src/utils/backupOnboardingLocalStorage';
import { BackupOnboardingRoutesEnum } from 'src/pages/BackupOnboarding';

import { createRequiredContext } from '../createRequiredContext';

import { useBackup } from './BackupContext';

const [useBackupOnboarding, BackupOnboardingProvider] = createRequiredContext<{
  handleNextStep: () => void;
  handlePrevStep: () => void;
  totalSteps: number;
  currentStep: number;
  lastCompletedStep: number;
  showBackButton: boolean;
}>();

// Define a linked list node structure
type IOnboardingStepNode = {
  step: IOnboardingStep;
  prev: IOnboardingStepNode | null;
  next: IOnboardingStepNode | null;
};

function buildLinkedList(): IOnboardingStepNode[] {
  const steps = ['details', 'members', 'notification-settings', 'storage-settings', 'summary'] as IOnboardingStep[];
  const nodes: IOnboardingStepNode[] = [];

  steps.forEach((step, i) => {
    const node = {
      step,
      prev: i > 0 ? nodes[i - 1] : null,
      next: null,
    };

    if (i > 0) {
      nodes[i - 1].next = node;
    }

    nodes.push(node);
  });

  return nodes;
}

const BackupOnboardingContextProvider = ({ children }: React.PropsWithChildren) => {
  const {
    backup: { id },
  } = useBackup();
  const navigate = useNavigate();

  const stepLinkedList = buildLinkedList();

  // Create a map for easy lookup by step name
  const stepLinkedListMap = stepLinkedList.reduce(
    (map, node) => {
      map[node.step] = node;
      return map;
    },
    {} as Record<IOnboardingStep, IOnboardingStepNode>,
  );

  const { '*': slug } = useParams<{ '*': BackupOnboardingRoutesEnum }>();

  const lastCompletedStep = getBackupOnboardingStep(id) || BackupOnboardingRoutesEnum.Members;
  const currentStep = (
    slug && slug in stepLinkedListMap ? slug : BackupOnboardingRoutesEnum.Members
  ) as IOnboardingStep;
  const currentStepIndex = stepLinkedList.findIndex((step) => step.step === currentStep) + 1;
  const lastCompletedStepIndex = stepLinkedList.findIndex((step) => step.step === lastCompletedStep) + 1;

  const handleNextStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const nextNode = currentNode.next;

    // Already at last step
    if (!nextNode) return;

    // Store the next step in the local storage
    if (currentStepIndex >= lastCompletedStepIndex) setBackupOnboardingStep(id, nextNode.step);

    // Build URL for next step
    const url = `/backups/onboarding/${id}/${nextNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate, lastCompletedStepIndex, currentStepIndex]);

  const handlePrevStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const prevNode = currentNode.prev;

    // Already at first step
    if (!prevNode) return;

    // Build URL for previous step
    const url = `/backups/onboarding/${id}/${prevNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate]);

  const providerValue = {
    handleNextStep,
    handlePrevStep,
    totalSteps: stepLinkedList.length,
    currentStep: currentStepIndex,
    lastCompletedStep: lastCompletedStepIndex,
    showBackButton: currentStepIndex > 1,
  };

  return <BackupOnboardingProvider value={providerValue}>{children}</BackupOnboardingProvider>;
};

export { useBackupOnboarding, BackupOnboardingContextProvider };
